<template>
  <v-dialog v-model="dialog" width="1200" @click:outside="close">
    <v-card>

      <v-alert
        v-model="alertFlg"
        type="error"
        density="compact"
        :title=errorMsg
        :text="$t('common.message.retry')"
        closable
        class="ma-2"
      />

      <v-card-title class="pa-4 px-5">
        {{ $t('tanaoroshiTerminalStatusDialog.label.title') }}
      </v-card-title>

      <v-card-text class="pa-4 px-5 grey--text text--darken-4 text-subtitle-1">
        <div class="d-flex align-center mb-2">
          <span class="mr-6">{{ $t('tanaoroshi.label.table.num') }}：{{ dialogData.tanaoroshi.num }}</span>
          <span class="mr-6">{{ $t('tanaoroshi.label.table.startDatetime') }}：{{ dialogData.tanaoroshi.startDatetime }}</span>
          <span class="mr-6">{{ $t('tanaoroshi.label.table.statusLabel') }}：{{ dialogData.tanaoroshi.statusLabel }}</span>
          <v-spacer></v-spacer>
          <span class="mr-4 text-body-2">{{ $t('tanaoroshiTerminalStatusDialog.label.searchedAt') }}：{{ searchedAt }}</span>
          <v-btn
            theme="dark"
            class="px-6 bg-grey-darken-3"
            elevation="2"
            :loading="isLoading"
            @click="search"
          >{{ $t('tanaoroshiTerminalStatusDialog.action.reload') }}</v-btn>
        </div>
        <p v-if="hasUnsaved" class="mb-2" style="color: #FF0000;">
          {{ $t('tanaoroshiTerminalStatusDialog.message.hasUnsaved') }}
        </p>
        <p class="mb-2 text-body-2">
          {{ $t('tanaoroshiTerminalStatusDialog.label.readQuantity') }}：{{ formatNumber(readQuantity) }}
          ／ {{ $t('tanaoroshiTerminalStatusDialog.label.epcCount') }}：{{ formatNumber(epcCount) }}
          <br>
          {{ $t('tanaoroshiTerminalStatusDialog.message.epcOnly') }}
        </p>
        <div class="table_wrap">
          <table>
            <thead>
              <tr>
                <th v-for="header in headers" :key="header">
                  {{ $t("tanaoroshiTerminalStatusDialog.label.table." + header) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="terminal in terminalList" :key="terminal.htSerial" :class="{ unsaved: isUnsaved(terminal) }">
                <td v-for="header in headers" :key="header">{{ terminal[header] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text @click="close"> {{ $t('tanaoroshiTerminalStatusDialog.action.close') }} </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/apis/staff";
import utils from "@/utils";

// 端末ステータスID（棚卸ステータスと同じ）
const terminalStatusId = {
  enforcing: 2, // 実施中（未保存）
}

export default {
  props: {
    dialogData: {
      type: Object,
      required: true,
    },
    handleClose: {
      type: Function,
      required: true,
    },
  },
  computed: {
    // ヘッダー
    headers() {
      return this.$getTableDef("TanaoroshiTerminalStatusDialog");
    },
    // 未保存の端末があるか
    hasUnsaved() {
      return this.terminalList.some(x => this.isUnsaved(x));
    },
  },
  data() {
    return {
      dialog: true,
      isLoading: false,
      errorMsg: "",
      alertFlg: false,
      terminalList: [],
      // 読取数量（棚卸全体。EPCの無い読取を含む）
      readQuantity: null,
      // 読取EPC数（棚卸全体）
      epcCount: null,
      searchedAt: "",
    };
  },
  mounted() {
    this.search();
  },
  methods: {
    isUnsaved(terminal) {
      return terminal.statusId == terminalStatusId.enforcing;
    },
    formatNumber(v) {
      return v == null ? "-" : Number(v).toLocaleString();
    },
    search() {
      this.isLoading = true;
      this.alertFlg = false;
      api.post("/tanaoroshi/terminal-status/search", {
        tanaoroshiId: this.dialogData.tanaoroshi.tanaoroshiId,
        shopCode: this.dialogData.shopCode ?? "",
      })
        .then((response) => {
          this.isLoading = false;
          if (response.data?.status != 0) {
            this.errorMsg = response.data?.errorMessage ?? this.$t('common.message.unknownError');
            this.alertFlg = true;
            return;
          }
          this.terminalList = (response.data.results?.terminalList ?? []).map(x => ({
            ...x,
            forceConfirmed: x.forceConfirmed ? this.$t('tanaoroshiTerminalStatusDialog.label.forceConfirmedMark') : "",
            startedDatetime: utils.putSplitWordToCalendarWithTime(x.startedDatetime),
            lastReadDatetime: utils.putSplitWordToCalendarWithTime(x.lastReadDatetime),
            savedDatetime: utils.putSplitWordToCalendarWithTime(x.savedDatetime),
            confirmedDatetime: utils.putSplitWordToCalendarWithTime(x.confirmedDatetime),
          }));
          this.readQuantity = response.data.results?.readQuantity ?? null;
          this.epcCount = response.data.results?.epcCount ?? null;
          this.searchedAt = utils.putSplitWordToCalendarWithTime(utils.csvTimeStamp(new Date()));
        })
        .catch((error) => {
          this.isLoading = false;
          this.errorMsg = this.$t('common.message.unknownError');
          this.alertFlg = true;
        });
    },
    close() {
      this.handleClose();
    },
  },
};
</script>

<style lang="scss" scoped>
.table_wrap {
  overflow: auto;
  max-height: 60vh;
  table {
    white-space: nowrap;
    border: solid 1px #bdbdbd;
    width: 100%;
    border-collapse: collapse;
    text-align: center;
    tr {
      &:not(:last-child) {
        border-bottom: 1px solid #eeeeee;
      }
      &.unsaved {
        background-color: #fff3e0;
      }
    }
    th {
      position: sticky;
      top: 0;
      padding: 10px;
      background-color: #3949ab;
      color: #fff;
    }
  }
  td {
    padding: 6px 8px;
  }
}
</style>
